// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetMedicineUsedAsDetail,
  CreateMedicineUsedAs,
  UpdateMedicineUsedAs,
} from "../../../services/MedicineUsedAsService";
import { PreferencesContext } from "../../../PreferenceContext";

export default function MedicineUsedAsSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = Props;

  interface formInitialValueTypes {
    medicineUsedAsId: number;
    consumerId: number;
    orgId: number;
    branchId: number;
    code: string;
    medicineUsedAsName: string;
    isLocked: boolean;
    isDeleted: boolean;
  }

  const initmedicineUsedAsData: formInitialValueTypes = {
    medicineUsedAsId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    code: "",
    medicineUsedAsName: isForPopUp ? name?.toUpperCase() : "",
    isLocked: false,
    isDeleted: false,
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawmedicineUsedAsId = urlParams.get("q");
  let curmedicineUsedAsId = 0;
  if (curRawmedicineUsedAsId !== null && curRawmedicineUsedAsId !== undefined) {
    let decId = edService.decrypt(curRawmedicineUsedAsId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curmedicineUsedAsId = +decId;
    }
  }
  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    medicineUsedAsId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    code: Yup.string(),
    medicineUsedAsName: Yup.string().required(
      "Medicine Used As Name is required"
    ),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initmedicineUsedAsData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET Medicine Used as DETAIL
  const getmedicineUsedAsDetailById = async () => {
    let medicineUsedAs: any;
    let params = new URLSearchParams();
    params.append("medicineUsedAsId", curmedicineUsedAsId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    try {
      const res: any = await GetMedicineUsedAsDetail(params);
      medicineUsedAs = res.data.data;
      methods.reset({
        ...initmedicineUsedAsData,
        medicineUsedAsId: medicineUsedAs?.medicineUsedAsId || 0,
        code: medicineUsedAs?.code || "",
        medicineUsedAsName: medicineUsedAs
          ? medicineUsedAs.medicineUsedAsName
          : isForPopUp
          ? name
          : "",
        isLocked: medicineUsedAs?.isLocked || false,
      });
      return medicineUsedAs;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE Medicine Diluent Name
  const savemedicineUsedAs = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        medicineUsedAsId: value.medicineUsedAsId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        code: value.code?.trim(),
        medicineUsedAsName: value.medicineUsedAsName?.trim(),
        isLocked: value.isLocked,
        isDeleted: value.isDeleted,
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curmedicineUsedAsId > 0) {
        result = await UpdateMedicineUsedAs(formData);
      } else {
        result = await CreateMedicineUsedAs(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result.data) {
            handleClosePopUp("MEDICINE_SOS_CREATE", result.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    savemedicineUsedAs(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    navigate(-1);
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curmedicineUsedAsId > 0) {
        const medicineUsedAs = await getmedicineUsedAsDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curmedicineUsedAsId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.code
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Code
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("code")}
                          error={!!methods.formState.errors.code}
                          className={`text-input ${methods.formState.errors.code ? "error" : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.code?.message}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    md={9}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  ></Grid> */}
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineUsedAsName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Medicine Used As *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          autoFocus
                          size="small"
                          {...methods.register("medicineUsedAsName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue(
                                "medicineUsedAsName",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              ); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.medicineUsedAsName}
                          className={`text-input ${
                            methods.formState.errors.medicineUsedAsName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.medicineUsedAsName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}
                  >
                    {curmedicineUsedAsId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={() =>
                      !isForPopUp ? handleBack() : handleClosePopUp("CLOSE")
                    }
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
